import { BlogTabs, type BlogTab } from '@/components/blog/BlogTabs';
import benchmark from '../../data/promptEngineeringBenchmark.json';

/**
 * InstructionLayerTabs — blog12, interactive item 1: what each arm was actually told.
 *
 * The four prompt-engineered arms show their system prompt verbatim (they are
 * empty or machine-generated — there is nothing proprietary in them, and seeing
 * that "extensive" really was extensive is the point).
 *
 * The Raisy Memory tab deliberately shows NO kernel content — only the BD/BDsub
 * coordinate and reuse/mint status of each loaded kernel, mirroring the
 * create-signature popup (AddBotModal) so the app and the blog speak one
 * vocabulary. Green = an existing library kernel was reused; amber = a kernel
 * was authored fresh for this bot. Ruled-out dimensions are shown dimmed: the
 * classifier considering a slot and declining it is itself the finding.
 */

type Slot = { dimension: string; sub: number | null; status: string; isCustom: boolean };
type Arm = { key: string; label: string; isRaisy: boolean; systemPrompt: string | null };

const PromptPanel = ({ prompt }: { prompt: string }) => {
  if (!prompt.trim()) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 p-6 text-center">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Empty system prompt</p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          The model received the task and nothing else.
        </p>
      </div>
    );
  }
  return (
    <>
      <pre className="max-h-80 sm:max-h-96 overflow-auto rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-3 sm:p-4 text-[10.5px] sm:text-[11.5px] leading-relaxed text-slate-800 dark:text-slate-200 whitespace-pre-wrap break-words">
        {prompt}
      </pre>
      <p className="mt-2 text-label-xs text-slate-500 dark:text-slate-400">
        {prompt.length.toLocaleString()} characters · generated by one LLM call from the
        meta-prompt, with only the adjective changed.
      </p>
    </>
  );
};

const MemoryPanel = () => {
  const mem = (benchmark as unknown as {
    raisyMemory: { classification: string; slots: Slot[]; ruledOut: string[] };
  }).raisyMemory;

  const mint = mem.slots.filter((s) => s.status === 'mint').length;
  const reuse = mem.slots.filter((s) => s.status === 'reuse').length;

  return (
    <>
      <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
        <div className="text-label-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Classification
        </div>
        <code className="mt-1 block break-all text-label-2xs sm:text-label-xs leading-relaxed text-emerald-700 dark:text-emerald-300">
          {mem.classification}
        </code>
        <div className="mt-1.5 text-label-xs text-slate-500 dark:text-slate-400">
          {reuse} kernel{reuse !== 1 ? 's' : ''} reused · {mint} minted for this bot ·{' '}
          {mem.ruledOut.length} dimensions ruled out
        </div>
      </div>

      <div className="mt-3 rounded-lg border border-slate-200 dark:border-slate-800 p-3">
        <div className="mb-2 text-label-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Kernel slots — BD999 → shared → BD1 → BD0
        </div>
        <div className="flex flex-wrap gap-1.5">
          {mem.slots.map((s) => (
            <span
              key={`${s.dimension}.${s.sub}`}
              title={`${s.isCustom ? 'custom' : 'shared'} · ${s.status}`}
              className={
                s.status === 'reuse'
                  ? 'rounded-full bg-emerald-500/10 px-2 py-0.5 text-label-2xs text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-500/20'
                  : 'rounded-full bg-amber-500/10 px-2 py-0.5 text-label-2xs text-amber-700 dark:text-amber-300 ring-1 ring-amber-500/20'
              }
            >
              {s.dimension}.{s.sub}
              <span className="opacity-55"> · {s.isCustom ? 'custom' : 'shared'}</span> · {s.status}
            </span>
          ))}
        </div>

        <div className="mt-3 mb-1.5 text-label-xs font-medium uppercase tracking-wide text-slate-600 dark:text-slate-500">
          Ruled out — considered, not applicable to this task
        </div>
        <div className="flex flex-wrap gap-1.5">
          {mem.ruledOut.map((d) => (
            <span
              key={d}
              className="rounded-full bg-slate-500/5 px-2 py-0.5 text-label-2xs text-slate-400 dark:text-slate-600"
            >
              BD{d}
            </span>
          ))}
        </div>
      </div>

      <p className="mt-3 text-label-xs text-slate-500 dark:text-slate-400">
        Kernel <em>content</em> is not shown. Each coordinate is an immutable, versioned kernel —
        the same slots the create-bot flow previews before it builds. The comparison arms send one
        flat string; this arm sends {mem.slots.length} addressable, individually versioned slots.
      </p>
    </>
  );
};

export const InstructionLayerTabs = () => {
  const arms = (benchmark as unknown as { arms: Arm[] }).arms;

  const tabs: BlogTab[] = arms.map((a) => ({
    key: a.key,
    label: a.label,
    accent: a.isRaisy,
    content: a.isRaisy ? <MemoryPanel /> : <PromptPanel prompt={a.systemPrompt ?? ''} />,
  }));

  return (
    <BlogTabs
      tabs={tabs}
      caption="What each arm received as its instruction layer. Everything else — model, temperature, judge, task text, context — is identical."
    />
  );
};
